const initialState = {
    // Production Earnings
    productionEarnings: [],
    productionEarningsSummary: null,
    
    // Daily Distributions
    dailyDistributions: [],
    dailyDistributionsSummary: null,
    
    // Daily Distribution Items
    dailyDistributionItems: [],
    availableEmployees: [],
    
    // Salary Statements
    salaryStatement: [],
    salaryStatementSummary: null,
    employeeEarnings: null,
    salaryStatementLoading: false,
    salaryStatementCurrentPage: 1,
    salaryStatementNumberOfPages: 1,
    salaryStatementTotal: 0,
    salaryStatementPerPage: 10,
    
    // Multiple days data structure
    multipleDaysData: {
        dateRange: null,
        dailyEarnings: {}, // {employeeId: {date: amount}}
        totalEarnings: {} // {date: totalAmount}
    },
    
    // Page Table State
    pageTable: {
        currentPage: 1,
        itemsPerPage: 10,
        manualCashAmounts: {}, // {employeeId: amount} or {employeeId_dateKey: amount}
        attendanceStatuses: {}, // {employeeId: status} or {employeeId_dateKey: status}
        editingStates: {}, // {employeeId: boolean} or {employeeId_dateKey: boolean}
        editingAmount: '',
        savingPageData: false,
        saveError: null
    },
    
    // UI State
    error: null
};

const salarySystemReducer = (state = initialState, action) => {
    switch (action.type) {
        // Production Earnings
        case 'SET_PRODUCTION_EARNINGS':
            return {
                ...state,
                productionEarnings: action.payload
            };
            
        case 'ADD_PRODUCTION_EARNING':
            return {
                ...state,
                productionEarnings: [action.payload, ...state.productionEarnings]
            };
            
        case 'UPDATE_PRODUCTION_EARNING':
            return {
                ...state,
                productionEarnings: state.productionEarnings.map(earning => 
                    earning.id === action.payload.id ? action.payload : earning
                )
            };
            
        case 'DELETE_PRODUCTION_EARNING':
            return {
                ...state,
                productionEarnings: state.productionEarnings.filter(earning => 
                    earning.id !== action.payload
                )
            };
            
        case 'SET_PRODUCTION_EARNINGS_SUMMARY':
            return {
                ...state,
                productionEarningsSummary: action.payload
            };
            
        // Daily Distributions
        case 'SET_DAILY_DISTRIBUTIONS':
            return {
                ...state,
                dailyDistributions: action.payload
            };
            
        case 'ADD_DAILY_DISTRIBUTION':
            return {
                ...state,
                dailyDistributions: [action.payload, ...state.dailyDistributions]
            };
            
        case 'UPDATE_DAILY_DISTRIBUTION':
            return {
                ...state,
                dailyDistributions: state.dailyDistributions.map(distribution => 
                    distribution.id === action.payload.id ? action.payload : distribution
                )
            };
            
        case 'DELETE_DAILY_DISTRIBUTION':
            return {
                ...state,
                dailyDistributions: state.dailyDistributions.filter(distribution => 
                    distribution.id !== action.payload
                )
            };
            
        case 'SET_DAILY_DISTRIBUTIONS_SUMMARY':
            return {
                ...state,
                dailyDistributionsSummary: action.payload
            };
            
        // Daily Distribution Items
        case 'SET_DAILY_DISTRIBUTION_ITEMS':
            return {
                ...state,
                dailyDistributionItems: action.payload
            };
            
        case 'ADD_DAILY_DISTRIBUTION_ITEM':
            return {
                ...state,
                dailyDistributionItems: [action.payload, ...state.dailyDistributionItems]
            };
            
        case 'ADD_DAILY_DISTRIBUTION_ITEMS':
            return {
                ...state,
                dailyDistributionItems: [...action.payload, ...state.dailyDistributionItems]
            };
            
        case 'UPDATE_DAILY_DISTRIBUTION_ITEM':
            return {
                ...state,
                dailyDistributionItems: state.dailyDistributionItems.map(item => 
                    item.id === action.payload.id ? action.payload : item
                )
            };
            
        case 'DELETE_DAILY_DISTRIBUTION_ITEM':
            return {
                ...state,
                dailyDistributionItems: state.dailyDistributionItems.filter(item => 
                    item.id !== action.payload
                )
            };
            
        case 'SET_AVAILABLE_EMPLOYEES':
            return {
                ...state,
                availableEmployees: action.payload
            };
            
        // Salary Statements
        case 'SET_SALARY_STATEMENT':
            return {
                ...state,
                salaryStatement: action.payload.data || action.payload,
                salaryStatementCurrentPage: action.payload.currentPage || state.salaryStatementCurrentPage,
                salaryStatementNumberOfPages: action.payload.numberOfPages || state.salaryStatementNumberOfPages,
                salaryStatementTotal: action.payload.total || state.salaryStatementTotal,
                salaryStatementPerPage: action.payload.perPage || state.salaryStatementPerPage
            };
            
        case 'SET_SALARY_STATEMENT_LOADING':
            return {
                ...state,
                salaryStatementLoading: action.payload
            };
            
        case 'SET_SALARY_STATEMENT_SUMMARY':
            return {
                ...state,
                salaryStatementSummary: action.payload
            };
            
        case 'SET_EMPLOYEE_EARNINGS':
            return {
                ...state,
                employeeEarnings: action.payload
            };
            
        // Multiple days data
        case 'SET_MULTIPLE_DAYS_DATA':
            return {
                ...state,
                multipleDaysData: action.payload
            };
            
        case 'UPDATE_DAILY_EARNINGS':
            return {
                ...state,
                multipleDaysData: {
                    ...state.multipleDaysData,
                    dailyEarnings: {
                        ...state.multipleDaysData.dailyEarnings,
                        ...action.payload
                    }
                }
            };
            
        // Page Table State
        case 'SET_CURRENT_PAGE':
            return {
                ...state,
                pageTable: {
                    ...state.pageTable,
                    currentPage: action.payload
                }
            };
            
        case 'SET_ITEMS_PER_PAGE':
            return {
                ...state,
                pageTable: {
                    ...state.pageTable,
                    itemsPerPage: action.payload
                }
            };
            
        case 'SET_MANUAL_CASH_AMOUNT':
            return {
                ...state,
                pageTable: {
                    ...state.pageTable,
                    manualCashAmounts: {
                        ...state.pageTable.manualCashAmounts,
                        [action.payload.key]: action.payload.amount
                    }
                }
            };
            
        case 'SET_ATTENDANCE_STATUS':
            return {
                ...state,
                pageTable: {
                    ...state.pageTable,
                    attendanceStatuses: {
                        ...state.pageTable.attendanceStatuses,
                        [action.payload.key]: action.payload.status
                    }
                }
            };
            
        case 'SET_EDITING_STATE':
            return {
                ...state,
                pageTable: {
                    ...state.pageTable,
                    editingStates: {
                        ...state.pageTable.editingStates,
                        [action.payload.key]: action.payload.isEditing
                    }
                }
            };
            
        case 'SET_EDITING_AMOUNT':
            return {
                ...state,
                pageTable: {
                    ...state.pageTable,
                    editingAmount: action.payload
                }
            };
            
        case 'SET_SAVING_PAGE_DATA':
            return {
                ...state,
                pageTable: {
                    ...state.pageTable,
                    savingPageData: action.payload
                }
            };
            
        case 'SET_SAVE_ERROR':
            return {
                ...state,
                pageTable: {
                    ...state.pageTable,
                    saveError: action.payload
                }
            };
            
        case 'CLEAR_MANUAL_CASH_AMOUNTS':
            return {
                ...state,
                pageTable: {
                    ...state.pageTable,
                    manualCashAmounts: {}
                }
            };
            
        case 'CLEAR_ATTENDANCE_STATUSES':
            return {
                ...state,
                pageTable: {
                    ...state.pageTable,
                    attendanceStatuses: {}
                }
            };
            
        case 'CLEAR_EDITING_STATES':
            return {
                ...state,
                pageTable: {
                    ...state.pageTable,
                    editingStates: {},
                    editingAmount: ''
                }
            };
            
        // UI State
        case 'SET_ERROR':
            return {
                ...state,
                error: action.payload
            };
            
        case 'CLEAR_ERROR':
            return {
                ...state,
                error: null
            };
            
        case 'CLEAR_SALARY_SYSTEM_DATA':
            return {
                ...initialState
            };
            
        default:
            return state;
    }
};

export default salarySystemReducer;
